import React, {FC, useState} from "react";
import {ApiResourceContextProviderProps, ApiResourceContextType} from "./types";
import {useFetchedResource} from "../api/hooks/useFetchedResource";
import {useApiService} from "../api/hooks/useApiService";
import {Contact} from "../api/dto/Contact";
import {Relation} from "../api/dto/Relation";
import {Project} from "../api/dto/Project";
import {AuditLog} from "../api/dto/AuditLog";
import {Employee} from "../api/dto/Employee";
import {Quote} from "../api/dto/Quote";
import {Ledger} from "../api/dto/Ledger";
import {VatRate} from "../api/dto/VatRate";
import {Order} from "../api/dto/Order";
import {Supplier} from "../api/dto/Supplier";
import {Vehicle} from "../api/dto/Vehicle";
import {ProjectType} from "../api/dto/ProjectType";
import {QuantityType} from "../api/dto/QuantityType";
import {ProjectVisit} from "../api/dto/ProjectVisit";
import {ZipcodeZone} from "../api/dto/ZipcodeZone";
import {useRefreshEffect} from "../realtime/RefreshController";
import {Color} from "../api/dto/Color";
import {ProjectVisitMapConfig} from "../api/dto/ProjectVisitMapConfig";
import {Workorder} from "../api/dto/Workorder";

export const ApiResourceContext = React.createContext<ApiResourceContextType>({} as ApiResourceContextType)

export const ApiResourceContextProvider: FC<ApiResourceContextProviderProps> = (props) => {

  // State
  const [contextDate, setContextDate] = useState(new Date())

  // Hooks
  const {contactService, relationService, vehicleService, projectService, quantityTypeService, auditLogService, employeeService, quoteService, financeService, orderService, projectTypeService, projectVisitService, zipcodeZoneService, colorService, workorderService} = useApiService(props.config, props.tokenResolver, props.navigateFactory())

  // Resources
  const contacts = useFetchedResource<Contact[]>(() => contactService.browse())
  const relations = useFetchedResource<Relation[]>(() => relationService.browse())
  const projects = useFetchedResource<Project[]>(() => projectService.browse())
  const auditLogs = useFetchedResource<AuditLog[]>(() => auditLogService.browse())
  const employees = useFetchedResource<Employee[]>(() => employeeService.browse())
  const quotes = useFetchedResource<Quote[]>(() => quoteService.browse())
  const ledgers = useFetchedResource<Ledger[]>(() => financeService.browseLedgers())
  const vatRates = useFetchedResource<VatRate[]>(() => financeService.browseVatRates())
  const orders = useFetchedResource<Order[]>(() => orderService.browseOrders())
  const suppliers = useFetchedResource<Supplier[]>(() => orderService.browseSuppliers())
  const vehicles = useFetchedResource<Vehicle[]>(() => vehicleService.browse())
  const projectTypes = useFetchedResource<ProjectType[]>(() => projectTypeService.browse())
  const quantityTypes = useFetchedResource<QuantityType[]>(() => quantityTypeService.browse())
  const projectVisits = useFetchedResource<ProjectVisit[]>(() => projectVisitService.browseProjectVisits())
  const zipcodeZones = useFetchedResource<ZipcodeZone[]>(() => zipcodeZoneService.browseZipcodeZones())
  const projectVisitMapConfigurations = useFetchedResource<ProjectVisitMapConfig[]>(() => projectVisitService.browseMapConfigurations())
  const colors = useFetchedResource<Color[]>(() => colorService.browse())
  const workorders = useFetchedResource<Workorder[], Date>((d) => workorderService.getWorkorders(d))

  // Initial load / reload
  useRefreshEffect('project', () => projects.reload(undefined), [])
  useRefreshEffect('contact', () => contacts.reload(undefined), [])
  useRefreshEffect('relation', () => relations.reload(undefined), [])
  useRefreshEffect('audit_log', () => auditLogs.reload(undefined), [])
  useRefreshEffect('employee', () => employees.reload(undefined), [])
  useRefreshEffect('quote', () => quotes.reload(undefined), [])
  useRefreshEffect('ledger', () => ledgers.reload(undefined), [])
  useRefreshEffect('vat_rate', () => vatRates.reload(undefined), [])
  useRefreshEffect('order', () => orders.reload(undefined), [])
  useRefreshEffect('supplier', () => suppliers.reload(undefined), [])
  useRefreshEffect('vehicle', () => vehicles.reload(undefined), [])
  useRefreshEffect('project_type', () => projectTypes.reload(undefined), [])
  useRefreshEffect('quantity_type', () => quantityTypes.reload(undefined), [])
  useRefreshEffect('project_visit', () => projectVisits.reload(undefined), [])
  useRefreshEffect('zipcode_zone', () => zipcodeZones.reload(undefined), [])
  useRefreshEffect('project_visit_map_configuration', () => projectVisitMapConfigurations.reload(undefined), [])
  useRefreshEffect('colors', () => colors.reload(undefined), [])
  useRefreshEffect('workorders', () => workorders.reload(contextDate), [])

  // Loading state
  const loadingStates = [
    projects.loading ? 0 : 1,
    relations.loading ? 0 : 1,
    contacts.loading ? 0 : 1,
    quotes.loading ? 0 : 1,
    orders.loading ? 0 : 1,
    suppliers.loading ? 0 : 1,
    projectVisits.loading ? 0 : 1,
    zipcodeZones.loading ? 0 : 1,
    projectVisitMapConfigurations.loading ? 0 : 1,
    colors.loading ? 0 : 1,
    workorders.loading ? 0 : 1,
  ]
  const finishedLoadingStates = loadingStates.reduce((previous, current) => previous + current, 0)
  const totalLoadingStates = loadingStates.length

  // Expose properties
  return <ApiResourceContext.Provider value={{

    // Resources
    contacts: contacts.resource ?? [],
    relations: relations.resource ?? [],
    projects: projects.resource ?? [],
    auditLogs: auditLogs.resource ?? [],
    employees: employees.resource ?? [],
    quotes: quotes.resource ?? [],
    ledgers: ledgers.resource ?? [],
    vatRates: vatRates.resource ?? [],
    orders: orders.resource ?? [],
    suppliers: suppliers.resource ?? [],
    vehicles: vehicles.resource ?? [],
    projectTypes: projectTypes.resource ?? [],
    quantityTypes: quantityTypes.resource ?? [],
    projectVisits: projectVisits.resource ?? [],
    zipcodeZones: zipcodeZones.resource ?? [],
    projectVisitMapConfigurations: projectVisitMapConfigurations.resource ?? [],
    colors: colors.resource ?? [],
    workorders: workorders.resource ?? [],

    // Loading state
    loading: finishedLoadingStates < totalLoadingStates,
    loadingProgress: Math.ceil(finishedLoadingStates / totalLoadingStates * 100),

    // Reload functions
    onProjectsUpdated: () => projects.reload(undefined),
    onContactsUpdated: () => contacts.reload(undefined),
    onRelationsUpdated: () => relations.reload(undefined),
    onAuditLogsUpdated: () => auditLogs.reload(undefined),
    onEmployeesUpdated: () => employees.reload(undefined),
    onQuoteUpdated: () => quotes.reload(undefined),
    onLedgerUpdated: () => ledgers.reload(undefined),
    onVatRateUpdated: () => vatRates.reload(undefined),
    onOrdersUpdated: () => orders.reload(undefined),
    onSuppliersUpdated: () => suppliers.reload(undefined),
    onProjectTypesUpdated: () => projectTypes.reload(undefined),
    onQuantityTypesUpdated: () => quantityTypes.reload(undefined),
    onProjectVisitsUpdated: () => projectVisits.reload(undefined),
    onZipcodeZonesUpdated: () => zipcodeZones.reload(undefined),
    onProjectVisitMapConfigurationsUpdated: () => projectVisitMapConfigurations.reload(undefined),
    onColorsUpdated: () => colors.reload(undefined),
    onWorkordersUpdated: () => workorders.reload(contextDate),

    // Context Date
    contextDate,
    setContextDate,

  }}>{props.children}</ApiResourceContext.Provider>
}